import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiRoutes } from './ApiRoutes';
import { Gt2ApiService } from './gt2-api.service';
import {
  ConvertSubmissionPayload,
  SubmissionModel,
} from './models/SubmissionModel';

export type SubmissionsListFilter = 'pending' | 'archived' | 'all';

@Injectable({
  providedIn: 'root',
})
export class SubmissionsService {
  constructor(
    private http: HttpClient,
    private api: Gt2ApiService,
  ) {}

  public getSubmissionsWithPagination(
    sort: string,
    direction: string,
    page: number,
    pageSize: number,
    filterValue: string,
    columnFilters: { [key: string]: string } = {},
    listFilter: SubmissionsListFilter = 'pending',
  ): Promise<any> {
    const routeURL: string = this.api.createUrl(ApiRoutes.SUBMISSIONS);
    let requestUrl: string;

    if (!sort || direction === '') {
      requestUrl = `${routeURL}?page=${page + 1}&limit=${pageSize}`;
    } else {
      requestUrl = `${routeURL}?orderBy=${sort}&sortedBy=${direction}&page=${page + 1}&limit=${pageSize}`;
    }

    // ?pending=1 → en attente, ?pending=0 → archivées, absent → toutes.
    if (listFilter === 'pending') {
      requestUrl += '&pending=1';
    } else if (listFilter === 'archived') {
      requestUrl += '&pending=0';
    }

    // Remove special characters from the filter value for proper searching
    filterValue = filterValue.replace(/%/g, '');

    const searchParts: string[] = [];
    if (filterValue !== '') {
      searchParts.push(filterValue);
    }

    // Column filters (field:value format)
    for (const [field, value] of Object.entries(columnFilters)) {
      searchParts.push(`${field}:${value}`);
    }

    if (searchParts.length > 0) {
      requestUrl += '&search=' + searchParts.join(';');
      if (searchParts.length > 1) {
        requestUrl += '&searchJoin=and';
      }
    }

    return new Promise((resolve, reject) => {
      this.http.get(requestUrl).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: reject,
      });
    });
  }

  public getSubmission(uuid: string): Observable<{ data: SubmissionModel }> {
    return this.http.get<{ data: SubmissionModel }>(
      this.api.createUrl(ApiRoutes.GET_SUBMISSION) + uuid,
    );
  }

  public deleteSubmission(uuid: string) {
    return this.http.delete<any>(
      this.api.createUrl(ApiRoutes.DELETE_SUBMISSION) + uuid,
    );
  }

  public restoreSubmission(uuid: string) {
    return this.http.post<any>(
      this.api.createUrl(ApiRoutes.RESTORE_SUBMISSION) + uuid + '/restore',
      {},
    );
  }

  /** Converts a pending submission into a Proposal/Order; resolves the created Event. */
  public convertSubmission(
    uuid: string,
    payload: ConvertSubmissionPayload,
  ): Observable<{ data: any }> {
    return this.http.post<{ data: any }>(
      this.api.createUrl(ApiRoutes.CONVERT_SUBMISSION) + uuid + '/convert',
      payload,
    );
  }

  public archiveSubmission(
    uuid: string,
    clientUuid?: string | null,
  ): Observable<{ data: SubmissionModel }> {
    return this.http.post<{ data: SubmissionModel }>(
      this.api.createUrl(ApiRoutes.ARCHIVE_SUBMISSION) + uuid + '/archive',
      clientUuid ? { client_uuid: clientUuid } : {},
    );
  }

  public unarchiveSubmission(
    uuid: string,
  ): Observable<{ data: SubmissionModel }> {
    return this.http.post<{ data: SubmissionModel }>(
      this.api.createUrl(ApiRoutes.UNARCHIVE_SUBMISSION) + uuid + '/unarchive',
      {},
    );
  }

  /** Menu badge: number of pending submissions (archived_at NULL). */
  public getPendingCount(): Observable<{ data: { count: number } }> {
    return this.http.get<{ data: { count: number } }>(
      this.api.createUrl(ApiRoutes.SUBMISSIONS_PENDING_COUNT),
    );
  }

  public getServiceTypes() {
    return this.http.get<any>(
      this.api.createUrl(ApiRoutes.SUBMISSION_SERVICE_TYPES),
    );
  }
}
